import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';

// 统一圆角变量
const BORDER_RADIUS = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

const getReadableTextColor = (backgroundColor) => {
  const hex = String(backgroundColor || '').trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return '#ffffff';
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const normalize = (value) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * normalize(r) + 0.7152 * normalize(g) + 0.0722 * normalize(b);
  return luminance > 0.45 ? '#111111' : '#ffffff';
};

// 主题预设定义 - 易于扩展新主题
export const THEMES = {
  default: {
    id: 'default',
    name: '默认暗色',
    description: '经典暗色主题，橙色强调色',
    borderRadius: BORDER_RADIUS,
    colors: {
      // 背景色 - 调亮一点，不再那么黑
      bgPrimary: '#1e1e22',      // 浅一点的深灰
      bgSecondary: '#2a2a2e',    // zinc-800 调亮
      bgTertiary: '#3a3a42',     // zinc-700 调亮
      bgOverlay: '#121214',
      bgCard: 'rgba(42, 42, 46, 0.7)', // 卡片背景调亮

      // 边框
      borderPrimary: 'rgba(255, 255, 255, 0.12)',
      borderSecondary: 'rgba(255, 255, 255, 0.06)',
      borderHover: 'rgba(255, 160, 60, 0.4)',

      // 文字
      textPrimary: '#f0f0f2',    // zinc-100
      textSecondary: '#b0b0b6',  // zinc-400 调亮
      textMuted: '#808088',      // zinc-500 调亮

      // 强调色 - 橙红色调，不再那么刺眼
      accentPrimary: '#c96a28',  // 柔和的橙色
      accentSecondary: '#e08a45', // 稍亮的橙色
      accentLight: 'rgba(201, 106, 40, 0.12)',
      accentGlow: 'rgba(201, 106, 40, 0.2)',

      // 状态色
      success: '#3d9e5a',
      warning: '#d4a744',
      error: '#d93a3a',
      info: '#4a7cc8',

      // 紫色 (视频相关)
      purplePrimary: '#8b5cc6',
      purpleLight: 'rgba(139, 92, 198, 0.15)',

      // 蓝色 (图片相关)
      bluePrimary: '#4a8bc8',
      blueLight: 'rgba(74, 139, 200, 0.15)',

      // 补充色
      greenPrimary: '#3d9e5a',
      greenLight: 'rgba(61, 158, 90, 0.12)',
      tealPrimary: '#2d9e8a',
      pinkPrimary: '#d06090',
      redLight: 'rgba(217, 58, 58, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.25)',
      cardHover: '0 12px 20px rgba(0, 0, 0, 0.35)',
      glow: '0 0 16px rgba(201, 106, 40, 0.15)',
    }
  },

  cyberpunk: {
    id: 'cyberpunk',
    name: '赛博朋克',
    description: '霓虹粉蓝配色，未来感十足',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#14141a',
      bgSecondary: '#1e1e2e',
      bgTertiary: '#2a2a3e',
      bgOverlay: '#0a0a0e',
      bgCard: 'rgba(30, 30, 46, 0.75)',

      borderPrimary: 'rgba(0, 255, 255, 0.2)',
      borderSecondary: 'rgba(255, 0, 128, 0.1)',
      borderHover: 'rgba(0, 255, 255, 0.4)',

      textPrimary: '#e0e0e0',
      textSecondary: '#9898aa',
      textMuted: '#6a6a7a',

      accentPrimary: '#00d4e0',  // 柔和青色
      accentSecondary: '#e60090', // 柔和粉色
      accentLight: 'rgba(0, 212, 224, 0.1)',
      accentGlow: 'rgba(0, 212, 224, 0.2)',

      success: '#00e090',
      warning: '#e0c060',
      error: '#e04060',
      info: '#0080d0',

      purplePrimary: '#d080e0',
      purpleLight: 'rgba(208, 128, 224, 0.12)',

      bluePrimary: '#0080d0',
      blueLight: 'rgba(0, 128, 208, 0.12)',

      // 补充色
      greenPrimary: '#00e090',
      greenLight: 'rgba(0, 224, 144, 0.12)',
      tealPrimary: '#00c0b0',
      pinkPrimary: '#e60090',
      redLight: 'rgba(224, 64, 96, 0.12)',
    },
    shadows: {
      card: '0 4px 16px rgba(0, 0, 0, 0.3)',
      cardHover: '0 0 24px rgba(0, 212, 224, 0.15)',
      glow: '0 0 16px rgba(0, 212, 224, 0.15)',
    }
  },

  nature: {
    id: 'nature',
    name: '自然绿',
    description: '清新绿色调，护眼舒适',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#1c201c',
      bgSecondary: '#263426',
      bgTertiary: '#304030',
      bgOverlay: '#101210',
      bgCard: 'rgba(38, 52, 38, 0.7)',

      borderPrimary: 'rgba(100, 160, 100, 0.2)',
      borderSecondary: 'rgba(100, 160, 100, 0.1)',
      borderHover: 'rgba(100, 180, 100, 0.4)',

      textPrimary: '#e0ebe0',
      textSecondary: '#a8baa8',
      textMuted: '#7a8a7a',

      accentPrimary: '#5a9e60',  // 柔和绿色
      accentSecondary: '#7abf80',
      accentLight: 'rgba(90, 158, 96, 0.12)',
      accentGlow: 'rgba(90, 158, 96, 0.18)',

      success: '#6abf6a',
      warning: '#d4c060',
      error: '#d65a5a',
      info: '#5080c0',

      purplePrimary: '#a870c0',
      purpleLight: 'rgba(168, 112, 192, 0.12)',

      bluePrimary: '#5090d0',
      blueLight: 'rgba(80, 144, 208, 0.12)',

      // 补充色
      greenPrimary: '#6abf6a',
      greenLight: 'rgba(106, 191, 106, 0.12)',
      tealPrimary: '#50b0a0',
      pinkPrimary: '#c06080',
      redLight: 'rgba(214, 90, 90, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.2)',
      cardHover: '0 12px 20px rgba(90, 158, 96, 0.12)',
      glow: '0 0 16px rgba(90, 158, 96, 0.12)',
    }
  },

  ocean: {
    id: 'ocean',
    name: '海洋蓝',
    description: '深邃蓝色调，专业沉稳',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#0f1a24',
      bgSecondary: '#163250',
      bgTertiary: '#1e4060',
      bgOverlay: '#080e14',
      bgCard: 'rgba(22, 50, 80, 0.7)',

      borderPrimary: 'rgba(80, 160, 200, 0.2)',
      borderSecondary: 'rgba(80, 160, 200, 0.1)',
      borderHover: 'rgba(80, 180, 220, 0.4)',

      textPrimary: '#e0eaf0',
      textSecondary: '#a0b0c0',
      textMuted: '#708090',

      accentPrimary: '#4090c0',  // 柔和蓝色
      accentSecondary: '#50a0d0',
      accentLight: 'rgba(64, 144, 192, 0.12)',
      accentGlow: 'rgba(64, 144, 192, 0.2)',

      success: '#40c0d0',
      warning: '#d4b850',
      error: '#d06060',
      info: '#5090c0',

      purplePrimary: '#9080d0',
      purpleLight: 'rgba(144, 128, 208, 0.12)',

      bluePrimary: '#4090c0',
      blueLight: 'rgba(64, 144, 192, 0.12)',

      // 补充色
      greenPrimary: '#40c0d0',
      greenLight: 'rgba(64, 192, 208, 0.12)',
      tealPrimary: '#30b0c0',
      pinkPrimary: '#c06090',
      redLight: 'rgba(208, 96, 96, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.3)',
      cardHover: '0 12px 20px rgba(64, 144, 192, 0.12)',
      glow: '0 0 16px rgba(64, 144, 192, 0.15)',
    }
  },

  sunset: {
    id: 'sunset',
    name: '落日金',
    description: '暖色调落日风格，温馨舒适',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#221a18',
      bgSecondary: '#3a2a26',
      bgTertiary: '#453230',
      bgOverlay: '#120e0c',
      bgCard: 'rgba(58, 42, 38, 0.7)',

      borderPrimary: 'rgba(220, 140, 80, 0.2)',
      borderSecondary: 'rgba(220, 140, 80, 0.1)',
      borderHover: 'rgba(240, 170, 100, 0.4)',

      textPrimary: '#f5ebe0',
      textSecondary: '#c8b8a0',
      textMuted: '#988878',

      accentPrimary: '#d08050',  // 柔和橙金
      accentSecondary: '#e0a060',
      accentLight: 'rgba(208, 128, 80, 0.12)',
      accentGlow: 'rgba(208, 128, 80, 0.2)',

      success: '#60b080',
      warning: '#d4b050',
      error: '#d06060',
      info: '#6080c0',

      purplePrimary: '#b080d0',
      purpleLight: 'rgba(176, 128, 208, 0.12)',

      bluePrimary: '#6090c0',
      blueLight: 'rgba(96, 144, 192, 0.12)',

      // 补充色
      greenPrimary: '#60b080',
      greenLight: 'rgba(96, 176, 128, 0.12)',
      tealPrimary: '#50a090',
      pinkPrimary: '#d07080',
      redLight: 'rgba(208, 96, 96, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.25)',
      cardHover: '0 12px 20px rgba(208, 128, 80, 0.12)',
      glow: '0 0 16px rgba(208, 128, 80, 0.15)',
    }
  },

  minimalist: {
    id: 'minimalist',
    name: '极简白',
    description: '简洁白色调，干净清爽',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#fafafa',
      bgSecondary: '#f0f0f2',
      bgTertiary: '#e5e5e7',
      bgOverlay: '#ffffff',
      bgCard: 'rgba(240, 240, 242, 0.85)',

      borderPrimary: 'rgba(0, 0, 0, 0.1)',
      borderSecondary: 'rgba(0, 0, 0, 0.06)',
      borderHover: 'rgba(0, 0, 0, 0.2)',

      textPrimary: '#1a1a1a',
      textSecondary: '#6a6a6a',
      textMuted: '#9a9a9a',

      accentPrimary: '#2a2a2a',  // 深灰
      accentSecondary: '#4a4a4a',
      accentLight: 'rgba(0, 0, 0, 0.04)',
      accentGlow: 'rgba(0, 0, 0, 0.08)',

      success: '#3a8a5a',
      warning: '#c4a030',
      error: '#c43a3a',
      info: '#3a6a9a',

      purplePrimary: '#8a5ac0',
      purpleLight: 'rgba(138, 90, 192, 0.08)',

      bluePrimary: '#3a6a9a',
      blueLight: 'rgba(58, 106, 154, 0.08)',

      // 补充色
      greenPrimary: '#3a8a5a',
      greenLight: 'rgba(58, 138, 90, 0.06)',
      tealPrimary: '#2a7a6a',
      pinkPrimary: '#a05070',
      redLight: 'rgba(196, 58, 58, 0.06)',
    },
    shadows: {
      card: '0 2px 6px rgba(0, 0, 0, 0.04)',
      cardHover: '0 8px 16px rgba(0, 0, 0, 0.06)',
      glow: '0 0 12px rgba(0, 0, 0, 0.06)',
    }
  },

  // 更多深色主题
  deepspace: {
    id: 'deepspace',
    name: '深空',
    description: '深邃蓝紫，神秘深邃',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#0d0d14',
      bgSecondary: '#161622',
      bgTertiary: '#1f1f2e',
      bgOverlay: '#08080c',
      bgCard: 'rgba(22, 22, 34, 0.75)',

      borderPrimary: 'rgba(120, 100, 200, 0.15)',
      borderSecondary: 'rgba(120, 100, 200, 0.08)',
      borderHover: 'rgba(150, 130, 220, 0.4)',

      textPrimary: '#e0e0f0',
      textSecondary: '#9090b0',
      textMuted: '#606080',

      accentPrimary: '#8870cc',
      accentSecondary: '#a890e0',
      accentLight: 'rgba(136, 112, 204, 0.12)',
      accentGlow: 'rgba(136, 112, 204, 0.2)',

      success: '#50b080',
      warning: '#d4b050',
      error: '#d05060',
      info: '#5070c0',

      purplePrimary: '#8870cc',
      purpleLight: 'rgba(136, 112, 204, 0.15)',

      bluePrimary: '#5070c0',
      blueLight: 'rgba(80, 112, 192, 0.15)',

      // 补充色
      greenPrimary: '#50b080',
      greenLight: 'rgba(80, 176, 128, 0.12)',
      tealPrimary: '#40a090',
      pinkPrimary: '#c060a0',
      redLight: 'rgba(208, 80, 96, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.3)',
      cardHover: '0 8px 24px rgba(136, 112, 204, 0.12)',
      glow: '0 0 16px rgba(136, 112, 204, 0.15)',
    }
  },

  matrix: {
    id: 'matrix',
    name: '矩阵',
    description: '经典绿黑，代码风格',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#0a0a0a',
      bgSecondary: '#111111',
      bgTertiary: '#1a1a1a',
      bgOverlay: '#050505',
      bgCard: 'rgba(17, 17, 17, 0.8)',

      borderPrimary: 'rgba(0, 255, 80, 0.15)',
      borderSecondary: 'rgba(0, 255, 80, 0.08)',
      borderHover: 'rgba(0, 255, 100, 0.4)',

      textPrimary: '#e0ffe0',
      textSecondary: '#80c080',
      textMuted: '#506050',

      accentPrimary: '#00ff50',
      accentSecondary: '#40ff80',
      accentLight: 'rgba(0, 255, 80, 0.08)',
      accentGlow: 'rgba(0, 255, 80, 0.2)',

      success: '#00ff60',
      warning: '#ccaa30',
      error: '#ff4050',
      info: '#40a0ff',

      purplePrimary: '#a060e0',
      purpleLight: 'rgba(160, 96, 224, 0.12)',

      bluePrimary: '#40a0ff',
      blueLight: 'rgba(64, 160, 255, 0.12)',

      // 补充色
      greenPrimary: '#00ff60',
      greenLight: 'rgba(0, 255, 96, 0.08)',
      tealPrimary: '#00cc80',
      pinkPrimary: '#ff40a0',
      redLight: 'rgba(255, 64, 80, 0.08)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 255, 80, 0.08)',
      cardHover: '0 8px 24px rgba(0, 255, 80, 0.12)',
      glow: '0 0 20px rgba(0, 255, 80, 0.15)',
    }
  },

  crimson: {
    id: 'crimson',
    name: '血红',
    description: '深红暗色调，强烈对比',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#1a0a0a',
      bgSecondary: '#221111',
      bgTertiary: '#2d1818',
      bgOverlay: '#0f0505',
      bgCard: 'rgba(34, 17, 17, 0.75)',

      borderPrimary: 'rgba(200, 80, 80, 0.15)',
      borderSecondary: 'rgba(200, 80, 80, 0.08)',
      borderHover: 'rgba(220, 100, 100, 0.4)',

      textPrimary: '#f0e0e0',
      textSecondary: '#c0a0a0',
      textMuted: '#806060',

      accentPrimary: '#c85050',
      accentSecondary: '#e06060',
      accentLight: 'rgba(200, 80, 80, 0.12)',
      accentGlow: 'rgba(200, 80, 80, 0.2)',

      success: '#60c080',
      warning: '#d4b040',
      error: '#e04040',
      info: '#4060c0',

      purplePrimary: '#a060c0',
      purpleLight: 'rgba(160, 96, 192, 0.12)',

      bluePrimary: '#4060c0',
      blueLight: 'rgba(64, 96, 192, 0.12)',

      // 补充色
      greenPrimary: '#60c080',
      greenLight: 'rgba(96, 192, 128, 0.12)',
      tealPrimary: '#50a080',
      pinkPrimary: '#e06080',
      redLight: 'rgba(224, 64, 64, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.3)',
      cardHover: '0 8px 24px rgba(200, 80, 80, 0.1)',
      glow: '0 0 16px rgba(200, 80, 80, 0.15)',
    }
  },

  charcoal: {
    id: 'charcoal',
    name: '炭灰',
    description: '纯深灰，低调内敛',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#1a1a1a',
      bgSecondary: '#242424',
      bgTertiary: '#2e2e2e',
      bgOverlay: '#121212',
      bgCard: 'rgba(36, 36, 36, 0.75)',

      borderPrimary: 'rgba(255, 255, 255, 0.08)',
      borderSecondary: 'rgba(255, 255, 255, 0.04)',
      borderHover: 'rgba(255, 255, 255, 0.2)',

      textPrimary: '#e8e8e8',
      textSecondary: '#a8a8a8',
      textMuted: '#686868',

      accentPrimary: '#d0d0d0',
      accentSecondary: '#e8e8e8',
      accentLight: 'rgba(255, 255, 255, 0.06)',
      accentGlow: 'rgba(255, 255, 255, 0.1)',

      success: '#50a070',
      warning: '#c0a040',
      error: '#d04040',
      info: '#4060a0',

      purplePrimary: '#9060b0',
      purpleLight: 'rgba(144, 96, 176, 0.12)',

      bluePrimary: '#4060a0',
      blueLight: 'rgba(64, 96, 160, 0.12)',

      // 补充色
      greenPrimary: '#50a070',
      greenLight: 'rgba(80, 160, 112, 0.08)',
      tealPrimary: '#408070',
      pinkPrimary: '#a06080',
      redLight: 'rgba(208, 64, 64, 0.08)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.25)',
      cardHover: '0 8px 24px rgba(0, 0, 0, 0.35)',
      glow: '0 0 16px rgba(255, 255, 255, 0.08)',
    }
  },

  // 极黑主题
  midnight: {
    id: 'midnight',
    name: '深夜',
    description: '极致暗色，接近纯黑',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#0a0a0a',
      bgSecondary: '#111111',
      bgTertiary: '#1a1a1a',
      bgOverlay: '#000000',
      bgCard: 'rgba(17, 17, 17, 0.8)',

      borderPrimary: 'rgba(255, 255, 255, 0.06)',
      borderSecondary: 'rgba(255, 255, 255, 0.03)',
      borderHover: 'rgba(255, 255, 255, 0.15)',

      textPrimary: '#e0e0e0',
      textSecondary: '#808080',
      textMuted: '#505050',

      accentPrimary: '#c08040',
      accentSecondary: '#e0a060',
      accentLight: 'rgba(192, 128, 64, 0.1)',
      accentGlow: 'rgba(192, 128, 64, 0.15)',

      success: '#40a060',
      warning: '#b09030',
      error: '#c04040',
      info: '#3060a0',

      purplePrimary: '#7050a0',
      purpleLight: 'rgba(112, 80, 160, 0.1)',

      bluePrimary: '#3060a0',
      blueLight: 'rgba(48, 96, 160, 0.1)',

      // 补充色
      greenPrimary: '#40a060',
      greenLight: 'rgba(64, 160, 96, 0.10)',
      tealPrimary: '#308060',
      pinkPrimary: '#a05070',
      redLight: 'rgba(192, 64, 64, 0.10)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.4)',
      cardHover: '0 8px 24px rgba(0, 0, 0, 0.5)',
      glow: '0 0 16px rgba(192, 128, 64, 0.1)',
    }
  },

  vintage: {
    id: 'vintage',
    name: '复古棕褐',
    description: '旧报纸质感，暖黄褐基调，怀旧复古',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#1e1a14',
      bgSecondary: '#2a2418',
      bgTertiary: '#3a3020',
      bgOverlay: '#14100a',
      bgCard: 'rgba(42, 36, 24, 0.8)',

      borderPrimary: 'rgba(180, 140, 90, 0.2)',
      borderSecondary: 'rgba(180, 140, 90, 0.1)',
      borderHover: 'rgba(200, 160, 100, 0.4)',

      textPrimary: '#e8d8c0',
      textSecondary: '#b0a088',
      textMuted: '#7a6a58',

      accentPrimary: '#b8863c',
      accentSecondary: '#d0a050',
      accentLight: 'rgba(184, 134, 60, 0.12)',
      accentGlow: 'rgba(184, 134, 60, 0.18)',

      success: '#7a9a50',
      warning: '#c4a040',
      error: '#b85040',
      info: '#6a80a0',

      purplePrimary: '#9070a0',
      purpleLight: 'rgba(144, 112, 160, 0.12)',

      bluePrimary: '#6a80a0',
      blueLight: 'rgba(106, 128, 160, 0.12)',

      // 补充色
      greenPrimary: '#7a9a50',
      greenLight: 'rgba(122, 154, 80, 0.12)',
      tealPrimary: '#6a9080',
      pinkPrimary: '#a06860',
      redLight: 'rgba(184, 80, 64, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.3)',
      cardHover: '0 12px 20px rgba(184, 134, 60, 0.1)',
      glow: '0 0 16px rgba(184, 134, 60, 0.12)',
    }
  },

  glacier: {
    id: 'glacier',
    name: '冰川蓝',
    description: '清冷通透的冰蓝白调，干净利落',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#0e1820',
      bgSecondary: '#162838',
      bgTertiary: '#1e3848',
      bgOverlay: '#080e14',
      bgCard: 'rgba(22, 40, 56, 0.75)',

      borderPrimary: 'rgba(120, 200, 230, 0.2)',
      borderSecondary: 'rgba(120, 200, 230, 0.1)',
      borderHover: 'rgba(140, 220, 250, 0.4)',

      textPrimary: '#e0f0f8',
      textSecondary: '#a0c0d0',
      textMuted: '#688898',

      accentPrimary: '#60b0d0',
      accentSecondary: '#80c0e0',
      accentLight: 'rgba(96, 176, 208, 0.1)',
      accentGlow: 'rgba(96, 176, 208, 0.18)',

      success: '#50c090',
      warning: '#d0c050',
      error: '#d06060',
      info: '#50a0d0',

      purplePrimary: '#9090d0',
      purpleLight: 'rgba(144, 144, 208, 0.12)',

      bluePrimary: '#50a0d0',
      blueLight: 'rgba(80, 160, 208, 0.12)',

      // 补充色
      greenPrimary: '#50c090',
      greenLight: 'rgba(80, 192, 144, 0.12)',
      tealPrimary: '#40b0b0',
      pinkPrimary: '#c07090',
      redLight: 'rgba(208, 96, 96, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.3)',
      cardHover: '0 12px 20px rgba(96, 176, 208, 0.1)',
      glow: '0 0 16px rgba(96, 176, 208, 0.15)',
    }
  },

  sakura: {
    id: 'sakura',
    name: '樱花粉',
    description: '柔和暖粉白调，日系清新，温暖舒适',
    borderRadius: BORDER_RADIUS,
    colors: {
      bgPrimary: '#241a1c',
      bgSecondary: '#302428',
      bgTertiary: '#3e3034',
      bgOverlay: '#180e10',
      bgCard: 'rgba(48, 36, 40, 0.75)',

      borderPrimary: 'rgba(220, 160, 170, 0.2)',
      borderSecondary: 'rgba(220, 160, 170, 0.1)',
      borderHover: 'rgba(240, 180, 190, 0.4)',

      textPrimary: '#f0e0e4',
      textSecondary: '#c0a8b0',
      textMuted: '#887078',

      accentPrimary: '#d08090',
      accentSecondary: '#e0a0a8',
      accentLight: 'rgba(208, 128, 144, 0.12)',
      accentGlow: 'rgba(208, 128, 144, 0.18)',

      success: '#70a870',
      warning: '#d0b850',
      error: '#d06060',
      info: '#6088b0',

      purplePrimary: '#b080b8',
      purpleLight: 'rgba(176, 128, 184, 0.12)',

      bluePrimary: '#6088b0',
      blueLight: 'rgba(96, 136, 176, 0.12)',

      // 补充色
      greenPrimary: '#70a870',
      greenLight: 'rgba(112, 168, 112, 0.12)',
      tealPrimary: '#60a090',
      pinkPrimary: '#d08090',
      redLight: 'rgba(208, 96, 96, 0.12)',
    },
    shadows: {
      card: '0 4px 12px rgba(0, 0, 0, 0.25)',
      cardHover: '0 12px 20px rgba(208, 128, 144, 0.1)',
      glow: '0 0 16px rgba(208, 128, 144, 0.15)',
    }
  },
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('app_theme');
    if (saved && THEMES[saved]) {
      return saved;
    }
    return 'default';
  });

  const theme = THEMES[currentTheme];

  // 应用主题 CSS 变量到 document
  useEffect(() => {
    const root = document.documentElement;
    const colors = theme.colors;
    const shadows = theme.shadows;
    const borderRadius = theme.borderRadius || BORDER_RADIUS;

    // 设置 CSS 变量
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
    });
    root.style.setProperty('--color-accent-contrast', colors.accentContrast || getReadableTextColor(colors.accentPrimary));

    Object.entries(shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
    });

    // 设置圆角变量
    Object.entries(borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-${key}`, value);
    });

    // 设置 data-theme 属性
    root.setAttribute('data-theme', currentTheme);

    // 保存到 localStorage
    localStorage.setItem('app_theme', currentTheme);
  }, [theme, currentTheme]);

  const switchTheme = useCallback((themeId) => {
    if (THEMES[themeId]) {
      setCurrentTheme(themeId);
    }
  }, []);

  const getTheme = useCallback(() => theme, [theme]);

  return (
    <ThemeContext.Provider value={{ currentTheme, switchTheme, getTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // 返回默认主题，避免崩溃
    return {
      currentTheme: 'default',
      switchTheme: () => {},
      getTheme: () => THEMES.default,
      themes: THEMES,
    };
  }
  return context;
};

// 导出主题配置供外部使用
export { THEMES as DEFAULT_THEMES };
